﻿// app state + constants
//
// IMPORTANT: This file is loaded as a plain <script>, not type="module".
// All top-level `let`/`const`/`function` declarations become globals shared
// across the other 17 files. Use `import type` only — runtime imports would
// break the multi-script global pattern.

import type {
  BillingModel,
  Client,
  Project,
  WorkSession,
  InvoiceRecord,
  User,
} from "../shared/types";

// ========== State ==========
let projects: Project[] = [];
let clients: Client[] = [];
let sessions: WorkSession[] = [];
let timerInterval: number | null = null;
let currentSession: WorkSession | null = null;
let currentView: string = "dashboard"; // dashboard or reports
let pendingAiChanges: any = null; // Store AI-suggested changes
let storedCleanupSuggestions: any[] = []; // Store cleanup suggestions for apply
let userTimezone: string = localStorage.getItem("userTimezone") || "America/Chicago"; // Default to Central Time
const COMPANY_NAME = "This is Your Bill"; // Your company name for invoices
let currentAuthUser: User | null = null;
let currentAuthType: string | null = null;
let adminCompaniesCache: any[] = [];
let taxRatesByState: Record<string, number> = {};
let invoices: InvoiceRecord[] = [];
let jobs: any[] = [];
let appointments: any[] = [];
let scheduleCurrentMonth: Date = new Date();
let scheduleSelectedDate: Date | null = null;
const US_STATE_CODES: string[] = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC",
];

interface TabMetaEntry {
  title: string;
  description: string;
  label?: string;
}

const TAB_META_BY_MODEL: Record<BillingModel, Record<string, TabMetaEntry>> = {
  hourly: {
    dashboard: {
      title: "Home",
      label: "Home",
      description: "Track time, review today’s work, and ask the billing assistant.",
    },
    clients: {
      title: "Clients",
      label: "Clients",
      description: "Your client directory. Each client can have one or more projects.",
    },
    projects: {
      title: "Projects",
      label: "Projects",
      description: "Per-client jobs with their own rate, status, and notes.",
    },
    schedule: {
      title: "Calendar",
      label: "Calendar",
      description: "Schedule visits and review appointments at a glance.",
    },
    tools: {
      title: "Invoices",
      label: "Invoices",
      description: "Build invoices, track jobs, and tidy up notes for billing.",
    },
    reports: {
      title: "Reports",
      label: "Reports",
      description: "See monthly earnings, hours, and client activity at a glance.",
    },
    settings: {
      title: "Settings",
      label: "Settings",
      description: "Theme, time zone, payment, email, and tax preferences.",
    },
    admin: {
      title: "Admin",
      label: "Admin",
      description: "Manage companies and users for your organization.",
    },
  },
  service_based: {
    dashboard: {
      title: "Today",
      label: "Today",
      description: "Start from today’s schedule, open jobs, and billing follow-up.",
    },
    clients: {
      title: "Customers",
      label: "Customers",
      description: "Your customer directory with contact details and active work.",
    },
    projects: {
      title: "Services",
      label: "Services",
      description: "Customer service records with pricing defaults, status, and notes.",
    },
    schedule: {
      title: "Schedule",
      label: "Schedule",
      description: "Plan visits, review today’s stops, and keep the calendar moving.",
    },
    tools: {
      title: "Operations",
      label: "Operations",
      description: "Track jobs, send invoices, and keep work moving from scheduled to paid.",
    },
    reports: {
      title: "Reports",
      label: "Reports",
      description: "Review billing and activity trends across customers and jobs.",
    },
    settings: {
      title: "Settings",
      label: "Settings",
      description: "Theme, time zone, payment, email, and tax preferences.",
    },
    admin: {
      title: "Admin",
      label: "Admin",
      description: "Manage companies and users for your organization.",
    },
  },
};

const TAB_META = TAB_META_BY_MODEL.hourly;

const BILLING_MODEL_VISIBLE_TABS: Record<BillingModel, string[]> = {
  hourly: ["dashboard", "clients", "projects", "tools", "reports", "settings", "admin"],
  service_based: ["dashboard", "clients", "projects", "schedule", "tools", "settings", "admin"],
};

function normalizeBillingModelValue(value: unknown): BillingModel {
  return value === "service_based" || value === "field_service" ? "service_based" : "hourly";
}

function currentBillingModel(): BillingModel {
  const company = (window as any).currentCompany || {};
  return normalizeBillingModelValue(company.billingModel ?? company.businessType);
}

function isTabVisibleForBillingModel(tabName: string): boolean {
  const model = currentBillingModel();
  const visibleTabs = BILLING_MODEL_VISIBLE_TABS[model] || BILLING_MODEL_VISIBLE_TABS.hourly;
  return visibleTabs.includes(tabName);
}

function getTabMetaForCurrentModel(tabName: string): TabMetaEntry {
  const model = currentBillingModel();
  const metaByModel = TAB_META_BY_MODEL[model] || TAB_META_BY_MODEL.hourly;
  return metaByModel[tabName] || TAB_META_BY_MODEL.hourly[tabName] || TAB_META_BY_MODEL.hourly.dashboard;
}

function getTabLabelForCurrentModel(tabName: string): string {
  const meta = getTabMetaForCurrentModel(tabName);
  return meta.label || meta.title;
}
